/**
 * The Apps tab's Code for one service: its files counted from the vfs tree of `services/<name>` (TREE_DEPTH levels,
 * dependency and build folders left out): how many, their size, the main file types. "Open Code" opens the service's
 * Code tab on its main file, through the Files link Traces uses (`?view=files&path=…`, hooks/useCodeLinks.js); it
 * needs the app's router, so it is left out where there is none.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';
import * as vfsClient from '../../../utils/vfsClient';
import { formatBytes } from '../../../services/SandboxService';
import { codeStats } from '../../../utils/serviceTabsModel';
import { codeSearch } from '../../traces/tracesModel';
import { Card, LoadState, OpenLink, plural, useLoaded } from './common';

export const TREE_DEPTH = 6;

function OpenCode({ path }) {
  const navigate = useNavigate();
  const location = useLocation();
  return <OpenLink label="Open Code" onOpen={() => navigate(`${location.pathname}${codeSearch(window.location.search, { path })}`)} testId="apps-open-code" />;
}
OpenCode.propTypes = { path: PropTypes.string.isRequired };

export default function CodeTab({ projectId, service, canOpen = true }) {
  const routed = useInRouterContext();
  const state = useLoaded(() => vfsClient.readTree(projectId, `services/${service}`, TREE_DEPTH), [projectId, service]);
  const stats = codeStats(state.value);
  const ready = !state.loading && !state.error;
  return (
    <div className="apps-tab-body" data-testid="apps-tabpanel-code">
      <Card testId="apps-code-stats" title="Code" wide>
        {!ready || stats.files === 0 ? <LoadState empty="No files yet." state={state} /> : (
          <>
            <p data-testid="apps-code-total">{plural(stats.files, 'file')} · {formatBytes(stats.bytes)}</p>
            <ul className="apps-rows">
              {stats.types.map((type) => (
                <li key={type.type}><span className="apps-row-name"><code>{type.type}</code></span><span className="apps-muted">{plural(type.count, 'file')} · {formatBytes(type.bytes)}</span></li>
              ))}
            </ul>
            <p className="apps-muted apps-small">Dependency and build folders (node_modules, dist, target…) are not counted.</p>
          </>
        )}
      </Card>
      {canOpen && routed && stats.entry && <OpenCode path={`services/${service}/${stats.entry}`} />}
    </div>
  );
}

CodeTab.propTypes = { canOpen: PropTypes.bool, projectId: PropTypes.string, service: PropTypes.string.isRequired };
